<template>
  <v-app :style="styles">
    <AppOverlay
      :active="overlayActive"
      :staff-only="staffOnly"
      :conga="conga"
      :user-ban="userBan"
      :shutting-down="shuttingDown"
      @add-time="onAddTime"
      @logout="logout"
    />

    <Navbar v-if="ready && !staffOnly" />

    <v-btn
      v-if="!$vuetify.breakpoint.xs"
      fab fixed tile
      bottom small :ripple="false"
      class="chat-btn" color="blue700"
      @click="onChatToggle(false)"
    >
      <fai :icon="['fad', 'comments-alt']" class="blue100--text text-body-1" />
    </v-btn>

    <SidebarChat v-if="ready && !staffOnly" />

    <!-- ACP -->
    <v-btn
      v-if="user && user.hasPermission('CAN_VIEW_ADMIN_PANEL')"
      fab fixed tile
      bottom small :ripple="false"
      class="admin-btn" color="primary700"
      @click="$modal.open('admin', {maxWidth: 1100})"
    >
      <fai :icon="['fas', 'cog']" class="primary100--text text-h6" />
    </v-btn>

    <XPOverlay v-if="user" />

    <v-main>
      <v-alert
        v-if="conga && conga.globalNotice"
        tile dense dismissible
        color="site-alert primary700 primary100--text mb-0"
      >
        <div class="d-flex align-center justify-center my-1">
          <fai :icon="['fad', 'siren-on']" class="text-h5" />
          <p class="mb-0 mx-4 text-caption lh-1-4" v-text="conga.globalNotice" />
        </div>
      </v-alert>

      <router-view v-if="ready && !staffOnly" />
      <Footer v-if="ready && !staffOnly" />
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import AppOverlay from '@/components/layout/AppOverlay'
import Navbar from '@/components/layout/Navbar'
import Sidebar from '@/components/layout/Sidebar'
import SidebarChat from '@/components/layout/SidebarChat'
import Footer from '@/components/layout/Footer'
import OnboardingMixin from '@/mixins/onboarding'

export default {
  metaInfo: {
    title: 'Home',
    titleTemplate: '%s :: bandit.camp - The true immersive Rust gaming experience',
  },
  name: 'App',
  components: {
    AppOverlay,
    Navbar,
    Sidebar,
    SidebarChat,
    Footer,
    XPOverlay: () => import('@/components/layout/XPOverlay'),
  },
  mixins: [OnboardingMixin],
  data: () => ({
    localReadyTimeout: null,
    localReady: false,
    bottomNavOpen: true,
  }),
  computed: {
    ...mapGetters({
      user: 'user/session',
      userBan: 'user/banned',
      conga: 'conga/conga',
      settings: 'settings/settings',
      ui: 'ui',
      itemDB: 'items/items',
      localeLoaded: 'localeLoaded',
      loaded: 'loaded',
      shuttingDown: 'shuttingDown',
    }),
    ready() {
      return this.loaded
        && !!this.itemDB
        && !!this.conga
        && !!this.localeLoaded
        && !this.shuttingDown
    },
    canBypassStaffMode() {
      return !!this.user?.hasPermission('CAN_BYPASS_STAFF_MODE')
    },
    staffOnly() {
      const { canBypassStaffMode } = this

      if (this.conga?.maintenance?.active) {
        if (!canBypassStaffMode) return true
      }

      if (this.conga?.staffOnly) {
        if (!canBypassStaffMode) return true
      }

      return false
    },
    overlayActive() {
      return !this.ready || !!this.userBan || this.staffOnly || !this.localReady
    },
    styles() {
      const { bottomNavOpen } = this
      const {
        top, left, right, bottom,
      } = this.$vuetify.application

      return {
        '--top': `${top}px`,
        '--right': `${right}px`,
        '--left': `${left}px`,
        '--bottom': bottomNavOpen ? `${bottom}px` : '0px',
      }
    },
  },
  watch: {
    'conga.maintenance.active': function fn(val, old) {
      if (old && !val && !this.canBypassStaffMode) {
        setTimeout(() => {
          window.location.reload()
        }, Math.random() * 5e3)
      }
    },
    'conga.staffOnly': function fn(val, old) {
      if (old && !val && !this.canBypassStaffMode) {
        setTimeout(() => {
          window.location.reload()
        }, Math.random() * 5e3)
      }
    },
    user(val, old) {
      if (val && !old) {
        // check ref code
        const refCode = localStorage.getItem('referredBy')

        if (refCode) {
          this.$modal.open('refcode', { maxWidth: 400 }, {
            code: refCode,
            redirect: true,
          })
          localStorage.removeItem('referredBy')
        }
      }

      const { authRedirect } = this.$route.query
      if (val && authRedirect) {
        const query = { ...this.$route.query }
        delete query.authRedirect
        this.$router.push({ path: authRedirect, query })
      }

      if (val?.name === 'roleplayer') {
        if (this.onboardingDisabled || this.onboardingHasSeen('name-change')) return
        this.$modal.open('change-name', { maxWidth: 340 })
        this.onboardingSetSeen('name-change')
      }
    },
  },
  created() {
    // show loading overlay for at least 1 second
    this.localReadyTimeout = setTimeout(() => {
      this.localReady = true
    }, 3000)

    // close sidebar on mobile
    if (this.ui.sidebar.open && this.$vuetify.breakpoint.xs) {
      this.setSidebarOpen(false)
    }
    if (this.ui.chat.open && this.$vuetify.breakpoint.xs) {
      this.setChatOpen(false)
    }
  },
  methods: {
    ...mapMutations({
      setSettings: 'settings/setSettings',
      setSidebarOpen: 'setSidebarOpen',
      setChatOpen: 'setChatOpen',
    }),
    ...mapActions({
      logout: 'user/logout',
    }),
    onAddTime(val) {
      clearTimeout(this.localReadyTimeout)

      this.localReadyTimeout = setTimeout(() => {
        this.localReady = true
      }, val)
    },
    onSidebarToggle(isMobile) {
      if (isMobile && !this.ui.sidebar.open && this.ui.chat.open) {
        this.setChatOpen(false)
      }

      this.setSidebarOpen(!this.ui.sidebar.open)
    },
    onChatToggle(isMobile) {
      if (isMobile && !this.ui.chat.open && this.ui.sidebar.open) {
        this.setSidebarOpen(false)
      }

      this.setChatOpen(!this.ui.chat.open)
    },
    onMobileGamesClick() {
      if (this.ui.chat.open) {
        this.setChatOpen(false)
      }
      if (this.ui.sidebar.open) {
        this.setSidebarOpen(false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.site-alert {
  position: relative;
  z-index: 2;
}

.chat-btn {
  bottom: calc(16px + var(--bottom));
  left: calc(16px + var(--left));
  transition: left 0.3s ease, bottom 0.3s ease;
  will-change: left;
}

.backpack-btn {
  right: calc(16px + var(--right));
  bottom: calc(16px + var(--bottom));
  transition: right 0.3s ease, bottom 0.3s ease;
  will-change: right;
}

.admin-btn {
  right: calc(16px + var(--right));
  bottom: calc(64px + var(--bottom));
  transition: right 0.3s ease, bottom 0.3s ease;
  will-change: right;
}

.sidebar {
  &.v-navigation-drawer--is-mobile {
    height: calc(100vh - var(--top)) !important;
  }
}
</style>
